import React, { useEffect, useState } from "react";
import { getToken, mintAppConnectCode } from "../api";
import { track } from "../utils/analytics";

/**
 * AppConnectPage — one-click account link-up for the macOS app, at /app-connect
 * (public route, mounted before App like /irab and /extension-connect).
 *
 * Simpler than ExtensionConnectPage: there is no extension to detect and no
 * postMessage handshake. A native app can't read this page's localStorage, so
 * the handover goes through a short-lived single-use code and a custom URL
 * scheme:
 *
 *   app  → opens this page with ?challenge=<b64url(sha256(verifier))>&state=…
 *   here → POST /api/app-connect/mint/ → { code }
 *   here → location = murajairab://connect?code=…&state=…
 *   app  → POST /api/app-connect/exchange/ { code, verifier } → { token }
 *
 * The token never appears in a URL, and the verifier never leaves the app, so
 * another app squatting the URL scheme can't redeem an intercepted code.
 */
export default function AppConnectPage() {
  const token = getToken();
  const params = new URLSearchParams(window.location.search);
  const challenge = params.get("challenge") || "";
  const state = params.get("state") || "";

  // phase: ready | minting | opened | error
  const [phase, setPhase] = useState("ready");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Connect the Mac app — مراجعة";
    track("app-connect-view", { loggedIn: !!token });
  }, [token]);

  const connect = async () => {
    setPhase("minting");
    setError("");
    track("app-connect-click", {});
    try {
      const data = await mintAppConnectCode(challenge);
      setCode(data.code);
      setPhase("opened");
      track("app-connect-success", {});
      // Hand off to the app. If it isn't installed nothing happens, which is
      // why the code stays on screen for the manual fallback below.
      const url = `murajairab://connect?code=${encodeURIComponent(data.code)}`
        + `&state=${encodeURIComponent(state)}`;
      window.location.href = url;
    } catch (e) {
      setError(e.message || "Something went wrong.");
      setPhase("error");
      track("app-connect-error", { error: e.message || "unknown" });
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the code is selectable on screen anyway */
    }
  };

  return (
    <div className="dark-scope min-h-screen flex flex-col" style={{ background: "var(--ink)" }}>
      <header className="flex items-center justify-between px-5 py-4 max-w-3xl w-full mx-auto">
        <a href="https://muraja.app" className="flex items-baseline gap-2 no-underline">
          <span className="font-amiri text-2xl text-dt-gold-bright">مراجعة</span>
          <span className="text-xs text-dt-parchment-dim tracking-wide">murājaʿa</span>
        </a>
      </header>

      <main className="flex-1 flex items-start justify-center px-3 sm:px-4 pb-8">
        <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-lg px-6 py-10 text-center mt-6">
          <p className="font-amiri text-3xl text-dt-parchment mb-1">ربط تطبيق ماك</p>
          <p className="text-sm text-dt-parchment-dim mb-8">
            Connect the Mac app to your murājaʿa account
          </p>

          {!token ? (
            <>
              <p className="text-sm text-dt-parchment mb-5">
                Sign in to your account first, then come back to this page to connect.
              </p>
              <a
                href={`/?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                className="inline-block px-5 py-2.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter] no-underline"
              >
                Sign in
              </a>
              {/* Signing in navigates away, so keep a way back that still
                  carries the challenge — without it the returning user gets a
                  challenge-free code and silently loses the verifier binding. */}
              <p className="text-xs text-dt-parchment-dim mt-5">
                Already signed in in another tab?{" "}
                <button
                  onClick={() => window.location.reload()}
                  className="text-dt-gold-bright hover:underline"
                >
                  Reload this page
                </button>
              </p>
            </>
          ) : phase === "opened" ? (
            <>
              <p className="text-lg text-dt-gold-bright mb-2">Opening Muraja Iʿrāb…</p>
              <p className="text-sm text-dt-parchment-dim mb-6">
                Your Mac should ask to open the app. Once it says “Connected”, you can
                close this tab.
              </p>
              <div className="text-left pt-5" style={{ borderTop: "1px solid var(--panel-line)" }}>
                <p className="text-xs text-dt-parchment-dim mb-2">
                  Didn't open? Paste this code into the app's Settings → Connect.
                  It expires in two minutes.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs text-dt-parchment bg-black/30 rounded-lg px-3 py-2 break-all select-all">
                    {code}
                  </code>
                  <button
                    onClick={copyCode}
                    className="px-3 py-2 rounded-lg text-dt-gold-bright text-xs font-semibold hover:brightness-125 transition-[filter]"
                    style={{ background: "var(--gold-bg)", border: "1px solid var(--panel-line)" }}
                  >
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <button
                  onClick={connect}
                  className="mt-4 text-xs text-dt-parchment-dim hover:text-dt-parchment underline"
                >
                  Get a new code
                </button>
              </div>
            </>
          ) : phase === "error" ? (
            <>
              <p className="text-sm text-dt-parchment mb-2">Couldn't create a connect code.</p>
              <p className="text-xs text-dt-parchment-dim mb-6">{error}</p>
              <button
                onClick={connect}
                className="px-5 py-2.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter]"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-dt-parchment mb-6">
                One click and the Mac app uses your plan's daily allowance —
                AI-checked iʿrāb in any app you read in.
              </p>
              <button
                onClick={connect}
                disabled={phase === "minting"}
                className="px-6 py-3 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter] disabled:opacity-60"
              >
                {phase === "minting" ? "Connecting…" : "Open Muraja Iʿrāb"}
              </button>
              <p className="text-xs text-dt-parchment-dim mt-5">
                Don't have the app yet?{" "}
                <a
                  href="https://muraja.app/mac/?utm_source=web_app&utm_medium=referral&utm_campaign=mac_app&utm_content=app_connect"
                  className="text-dt-gold-bright hover:underline"
                >
                  Download it for macOS
                </a>
              </p>
            </>
          )}
        </div>
      </main>

      <footer className="px-5 py-4 text-center text-xs text-dt-parchment-dim space-x-3">
        <a href="https://muraja.app" className="hover:text-dt-parchment">muraja.app</a>
        <a href="https://muraja.app/privacy.html" className="hover:text-dt-parchment">Privacy</a>
      </footer>
    </div>
  );
}
